import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Virtual Data Table Component
 * Drop-in replacement for DataTable with virtual scrolling for large datasets
 * Maintains 100% API and design compatibility
 */
import React, { useState, useEffect, useMemo, useCallback, useContext, useRef } from 'react';
import { cn } from '../../utils/cn';
import { ThemeContext } from '../../contexts/theme-context';
import { DEFAULT_THEME } from '../../types/theme';
import { Button } from '../button';
import { Input } from '../input';
import { Select } from '../select';
import { VirtualTableCore } from './virtual-table-core';
import { X, Filter } from 'lucide-react';
/**
 * Performance Note: If animations cause performance issues with virtual scrolling,
 * you can disable them by setting a prop or using this alternative approach:
 *
 * 1. Replace CSS transitions with instant show/hide:
 *    Remove: "transition-all duration-300 ease-in-out"
 *    Keep only: showFilters ? "block" : "hidden"
 *
 * 2. Use requestAnimationFrame for smoother transitions:
 *    const [animatingFilters, setAnimatingFilters] = useState(false);
 *
 *    const toggleFilters = () => {
 *      setAnimatingFilters(true);
 *      requestAnimationFrame(() => {
 *        setShowFilters(!showFilters);
 *        requestAnimationFrame(() => setAnimatingFilters(false));
 *      });
 *    };
 *
 * 3. Add a prop to control animations:
 *    enableAnimations?: boolean (default: true)
 *    Then conditionally apply animation classes based on this prop
 */
export const VirtualDataTable = React.forwardRef(({ title, description, data, columns, filters = [], actions = [], bulkActions = [], searchable = true, sortable = true, selectable = false, pagination, onAdd, addLabel = "Add New", className, loading = false, bulkActionsStyle = 'toolbar', onSearch, onFilterChange, serverSide = false, height, maxHeight, autoHeight = false, heightOffset = 180, 
// Virtual scrolling props
virtualScrolling = false, itemHeight, overscan = 5, virtualThreshold = 100, estimateRowHeight, onVirtualRangeChange, enableAnimations = true, 
// Grouping props
grouping, ...props }, ref) => {
    // Use context directly to avoid error when no provider
    const context = useContext(ThemeContext);
    const theme = context ? context.theme : DEFAULT_THEME;
    const isCompact = theme.layoutDensity === 'compact';
    // State management - EXACT same as original DataTable
    const [searchTerm, setSearchTerm] = useState('');
    const [filterValues, setFilterValues] = useState({});
    const [appliedFilterValues, setAppliedFilterValues] = useState({});
    const [sortConfig, setSortConfig] = useState(null);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [showFilters, setShowFilters] = useState(false);
    const [showColumnVisibility, setShowColumnVisibility] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState(new Set(columns.filter(col => !col.hidden).map(col => col.key)));
    // Grouping state
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    // Auto-enable virtual scrolling for large datasets
    const shouldUseVirtual = virtualScrolling || data.length >= virtualThreshold;
    // Ref for column visibility dropdown
    const columnDropdownRef = useRef(null);
    // Click-away handler for column dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target)) {
                setShowColumnVisibility(false);
            }
        };
        if (showColumnVisibility) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showColumnVisibility]);
    // Calculate container height - SAME logic as original
    const containerHeight = useMemo(() => {
        if (height)
            return height;
        if (autoHeight) {
            const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
            const calculatedHeight = viewportHeight - heightOffset;
            return `${Math.max(300, calculatedHeight)}px`;
        }
        return shouldUseVirtual ? '500px' : undefined; // Default height for virtual mode
    }, [height, autoHeight, heightOffset, shouldUseVirtual]);
    // Helper functions for grouping
    const calculateSummary = useCallback((items, config) => {
        if (!items.length)
            return 0;
        switch (config.type) {
            case 'sum':
                return items.reduce((sum, item) => sum + (Number(item[config.field]) || 0), 0);
            case 'avg':
                const total = items.reduce((sum, item) => sum + (Number(item[config.field]) || 0), 0);
                return total / items.length;
            case 'count':
                return items.length;
            case 'min':
                return Math.min(...items.map(item => Number(item[config.field]) || 0));
            case 'max':
                return Math.max(...items.map(item => Number(item[config.field]) || 0));
            case 'custom':
                return config.customCalculation ? config.customCalculation(items) : 0;
            default:
                return 0;
        }
    }, []);
    const createGroupKey = useCallback((values, level) => {
        return values.slice(0, level + 1).join('|');
    }, []);
    const toggleGroupExpansion = useCallback((groupKey) => {
        setExpandedGroups(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(groupKey)) {
                newExpanded.delete(groupKey);
            }
            else {
                newExpanded.add(groupKey);
            }
            grouping?.onGroupToggle?.(groupKey, newExpanded.has(groupKey));
            return newExpanded;
        });
    }, [grouping]);
    // Filter and search logic - EXACT same as original
    const filteredData = useMemo(() => {
        if (serverSide)
            return data;
        let filtered = [...data];
        // Apply search
        if (searchTerm && searchable) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(item => columns.some(column => {
                const value = item[column.key];
                return String(value).toLowerCase().includes(searchLower);
            }));
        }
        // Apply filters
        Object.entries(appliedFilterValues).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
                filtered = filtered.filter(item => {
                    const itemValue = item[key];
                    if (Array.isArray(value)) {
                        return value.includes(itemValue);
                    }
                    return String(itemValue).toLowerCase().includes(String(value).toLowerCase());
                });
            }
        });
        return filtered;
    }, [data, searchTerm, appliedFilterValues, columns, searchable, serverSide]);
    // Sort logic - EXACT same as original
    const sortedData = useMemo(() => {
        if (serverSide || !sortConfig)
            return filteredData;
        return [...filteredData].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];
            if (aVal === bVal)
                return 0;
            let comparison = 0;
            if (aVal == null)
                comparison = 1;
            else if (bVal == null)
                comparison = -1;
            else if (typeof aVal === 'string' && typeof bVal === 'string') {
                comparison = aVal.localeCompare(bVal);
            }
            else {
                comparison = aVal < bVal ? -1 : 1;
            }
            return sortConfig.direction === 'desc' ? -comparison : comparison;
        });
    }, [filteredData, sortConfig, serverSide]);
    // Grouping transformation
    const groupedData = useMemo(() => {
        if (!grouping?.enabled || !grouping.groups.length) {
            // Return original data as GroupedRow format for consistency
            return sortedData.map((item, index) => ({
                type: 'data',
                level: 0,
                data: item,
                groupKey: undefined,
                groupValue: undefined,
                groupLabel: undefined,
                isExpanded: undefined,
                childCount: undefined,
                summary: undefined
            }));
        }
        const result = [];
        const groupMap = new Map();
        // Initialize expanded groups if default is set
        if (expandedGroups.size === 0 && grouping.defaultExpanded) {
            const allGroupKeys = new Set();
            // Pre-calculate all possible group keys
            sortedData.forEach(item => {
                for (let level = 0; level < grouping.groups.length; level++) {
                    const values = grouping.groups.slice(0, level + 1).map(g => item[g.field]);
                    const groupKey = createGroupKey(values, level);
                    allGroupKeys.add(groupKey);
                }
            });
            setExpandedGroups(allGroupKeys);
        }
        // Build group hierarchy
        sortedData.forEach(item => {
            let currentMap = groupMap;
            const groupValues = [];
            // Build nested group structure
            for (let level = 0; level < grouping.groups.length; level++) {
                const groupConfig = grouping.groups[level];
                const groupValue = item[groupConfig.field];
                groupValues.push(groupValue);
                const groupKey = createGroupKey(groupValues, level);
                if (!currentMap.has(groupKey)) {
                    currentMap.set(groupKey, {
                        items: [],
                        children: new Map()
                    });
                }
                const group = currentMap.get(groupKey);
                group.items.push(item);
                currentMap = group.children;
            }
        });
        // Convert group hierarchy to flat array for virtual scrolling
        const addGroupToResult = (map, level, parentExpanded = true) => {
            map.forEach((group, groupKey) => {
                const groupConfig = grouping.groups[level];
                const groupValues = groupKey.split('|');
                const groupValue = groupValues[level];
                const isExpanded = expandedGroups.has(groupKey);
                const groupLabel = groupConfig.formatter
                    ? groupConfig.formatter(groupValue)
                    : `${groupConfig.label || groupConfig.field}: ${groupValue}`;
                // Add group header with summary (only if parent is expanded)
                if (parentExpanded) {
                    // Calculate selected count for this group
                    const selectedCount = group.items.filter(item => selectedRows.has(String(item.id))).length;
                    // Push group header row (clean, without summary)
                    result.push({
                        type: 'group',
                        level,
                        groupKey,
                        groupValue,
                        groupLabel,
                        isExpanded,
                        childCount: group.items.length,
                        selectedCount,
                        data: undefined,
                        summary: undefined // No summary in header row
                    });
                    // Push separate summary row if enabled and group is collapsed
                    if (grouping.showGroupSummary && grouping.summaryConfig?.length && !isExpanded) {
                        const groupSummary = {};
                        grouping.summaryConfig.forEach(config => {
                            const value = calculateSummary(group.items, config);
                            groupSummary[config.field] = value;
                        });
                        result.push({
                            type: 'group-summary',
                            level: level,
                            groupKey: `${groupKey}-summary`,
                            groupValue,
                            groupLabel: `Summary for ${groupLabel}`,
                            isExpanded: false,
                            childCount: group.items.length,
                            data: undefined,
                            summary: groupSummary
                        });
                    }
                }
                // Add child groups if this group is expanded
                if (isExpanded && parentExpanded) {
                    if (group.children.size > 0) {
                        // Recursive call for nested groups
                        addGroupToResult(group.children, level + 1, true);
                    }
                    else {
                        // Add data rows for leaf groups
                        group.items.forEach(item => {
                            result.push({
                                type: 'data',
                                level: level + 1,
                                data: item,
                                groupKey: undefined,
                                groupValue: undefined,
                                groupLabel: undefined,
                                isExpanded: undefined,
                                childCount: undefined,
                                summary: undefined
                            });
                        });
                    }
                    // Add group summary row at the end when expanded (after all data items)
                    if (grouping.showGroupSummary && grouping.summaryConfig?.length) {
                        const groupSummary = {};
                        grouping.summaryConfig.forEach(config => {
                            const value = calculateSummary(group.items, config);
                            groupSummary[config.field] = value;
                        });
                        result.push({
                            type: 'group-summary',
                            level: level,
                            groupKey: `${groupKey}-summary`,
                            groupValue,
                            groupLabel: `Summary for ${groupLabel}`,
                            isExpanded: true,
                            childCount: group.items.length,
                            data: undefined,
                            summary: groupSummary
                        });
                    }
                }
            });
        };
        addGroupToResult(groupMap, 0);
        // Add grand summary if enabled
        if (grouping.showGrandSummary && grouping.summaryConfig?.length) {
            const grandSummary = {};
            // Calculate grand totals with formatted values
            grouping.summaryConfig.forEach(config => {
                const value = calculateSummary(sortedData, config);
                grandSummary[config.field] = value;
            });
            result.push({
                type: 'summary',
                level: -1, // Special level for grand total
                summary: grandSummary,
                data: undefined,
                groupKey: 'grand-total',
                groupValue: undefined,
                groupLabel: '🎯 GRAND TOTAL',
                isExpanded: undefined,
                childCount: undefined
            });
        }
        return result;
    }, [sortedData, grouping, expandedGroups, selectedRows, calculateSummary, createGroupKey]);
    // Event handlers - SAME as original
    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        if (onSearch) {
            onSearch(term);
        }
    }, [onSearch]);
    const handleFilterChange = useCallback((key, value) => {
        const newValues = { ...filterValues, [key]: value };
        setFilterValues(newValues);
    }, [filterValues]);
    const handleApplyFilters = useCallback(() => {
        setAppliedFilterValues({ ...filterValues });
        if (onFilterChange) {
            onFilterChange(filterValues);
        }
    }, [filterValues, onFilterChange]);
    const handleClearFilters = useCallback(() => {
        setFilterValues({});
        setAppliedFilterValues({});
        if (onFilterChange) {
            onFilterChange({});
        }
    }, [onFilterChange]);
    const handleSort = useCallback((key, direction) => {
        setSortConfig({ key, direction });
    }, []);
    const handleRowSelect = useCallback((item, selected) => {
        setSelectedRows(prev => {
            const newSet = new Set(prev);
            const itemId = String(item.id);
            if (selected) {
                newSet.add(itemId);
            }
            else {
                newSet.delete(itemId);
            }
            return newSet;
        });
    }, []);
    const handleSelectAll = useCallback((selected, items) => {
        if (selected) {
            // For virtual table, always select ALL data items regardless of group visibility
            // This ensures consistent behavior whether groups are collapsed or expanded
            setSelectedRows(new Set(sortedData.map(item => String(item.id))));
        }
        else {
            setSelectedRows(new Set());
        }
    }, [sortedData]);
    // Bulk actions - works with original data regardless of grouping
    const hasSelectedRows = selectedRows.size > 0;
    const selectedItems = sortedData.filter(item => selectedRows.has(String(item.id)));
    // Filter visible columns - SAME as original DataTable
    const visibleColumnsArray = columns.filter(col => visibleColumns.has(col.key));
    // Toggle column visibility
    const handleColumnVisibilityChange = useCallback((columnKey) => {
        const newVisibleColumns = new Set(visibleColumns);
        if (newVisibleColumns.has(columnKey)) {
            newVisibleColumns.delete(columnKey);
        }
        else {
            newVisibleColumns.add(columnKey);
        }
        setVisibleColumns(newVisibleColumns);
    }, [visibleColumns]);
    return (_jsxs("div", { ref: ref, className: cn("bg-theme-primary border border-theme-border rounded-lg overflow-hidden", className), ...props, children: [(title || description || onAdd || (searchable && !serverSide) || filters.length > 0) && (_jsxs("div", { className: cn("border-b border-theme-border", isCompact ? "p-4" : "p-6"), children: [(title || description) && (_jsxs("div", { className: cn("mb-4", !onAdd && !searchable && filters.length === 0 && "mb-0"), children: [title && (_jsx("h3", { className: cn("font-semibold text-theme-foreground", isCompact ? "text-lg" : "text-xl"), children: title })), description && (_jsx("p", { className: cn("text-theme-muted mt-1", isCompact ? "text-sm" : "text-base"), children: description }))] })), (onAdd || (searchable && !serverSide) || filters.length > 0) && (_jsxs("div", { className: "flex items-center justify-between gap-4", children: [_jsx("div", { className: "flex items-center gap-2", children: searchable && !serverSide && (_jsx("div", { className: "relative", children: _jsx(Input, { type: "text", placeholder: "Search...", value: searchTerm, onChange: (e) => handleSearch(e.target.value), className: cn("w-64", isCompact && "text-sm") }) })) }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs("div", { className: "relative", ref: columnDropdownRef, children: [_jsxs(Button, { variant: "outline", size: isCompact ? "sm" : "default", onClick: () => setShowColumnVisibility(!showColumnVisibility), className: cn("flex items-center gap-2", visibleColumns.size < columns.length && "bg-theme-accent/10 border-theme-accent"), children: [_jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" }) }), _jsx("span", { children: "Columns" })] }), showColumnVisibility && (_jsxs("div", { className: "absolute right-0 top-full mt-2 w-56 bg-theme-primary border border-theme-border rounded-lg shadow-lg z-20 p-3", children: [_jsxs("div", { className: "flex items-center justify-between mb-2", children: [_jsx("div", { className: "text-sm font-medium text-theme-foreground", children: "Show/Hide Columns" }), _jsx("button", { onClick: () => setShowColumnVisibility(false), className: "text-theme-muted hover:text-theme-foreground", children: _jsx(X, { className: "h-4 w-4" }) })] }), _jsx("div", { className: "space-y-1 max-h-64 overflow-y-auto", children: columns.map((column) => (_jsxs("label", { className: "flex items-center gap-2 p-2 hover:bg-theme-secondary/50 rounded cursor-pointer", children: [_jsx("input", { type: "checkbox", checked: visibleColumns.has(column.key), onChange: () => handleColumnVisibilityChange(column.key), className: "rounded border-theme-border" }), _jsx("span", { className: "text-sm text-theme-foreground", children: column.label })] }, column.key))) }), _jsxs("div", { className: "mt-2 pt-2 border-t border-theme-border flex gap-2", children: [_jsx(Button, { variant: "ghost", size: "sm", onClick: () => setVisibleColumns(new Set(columns.map(col => col.key))), className: "flex-1 text-xs", children: "Show All" }), _jsx(Button, { variant: "ghost", size: "sm", onClick: () => setVisibleColumns(new Set()), className: "flex-1 text-xs", children: "Hide All" })] })] }))] }), filters.length > 0 && (_jsxs(Button, { variant: "outline", size: isCompact ? "sm" : "default", onClick: () => setShowFilters(!showFilters), className: cn("flex items-center gap-2", Object.keys(appliedFilterValues).length > 0 && "bg-theme-accent/10 border-theme-accent"), children: [_jsx(Filter, { className: "w-4 h-4" }), _jsx("span", { children: showFilters ? 'Hide Filters' : 'Show Filters' }), Object.keys(appliedFilterValues).length > 0 && (_jsx("span", { className: "ml-1 bg-theme-accent text-theme-accent-foreground rounded-full px-2 py-0.5 text-xs", children: Object.keys(appliedFilterValues).length }))] })), onAdd && (_jsxs(Button, { onClick: onAdd, size: isCompact ? "sm" : "default", className: "flex items-center gap-2", children: [_jsx("span", { children: "+" }), addLabel] }))] })] })), _jsx("div", { className: cn("overflow-hidden", enableAnimations && "transition-all duration-300 ease-in-out", showFilters && filters.length > 0
                            ? enableAnimations
                                ? "max-h-96 opacity-100"
                                : "block"
                            : enableAnimations
                                ? "max-h-0 opacity-0"
                                : "hidden"), children: filters.length > 0 && (_jsxs("div", { className: cn("border-t border-theme-border mt-4 pt-4 space-y-4"), children: [_jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4", children: filters.map((filter) => (_jsxs("div", { children: [_jsx("label", { className: cn("block text-theme-foreground font-medium mb-2", isCompact ? "text-sm" : "text-base"), children: filter.label }), filter.type === 'select' ? (_jsx(Select, { value: filter.options?.find(opt => opt.value === (filterValues[filter.key] || '')) || { value: '', label: 'All' }, onChange: (option) => handleFilterChange(filter.key, option?.value || ''), placeholder: filter.placeholder, options: [
                                                    { value: '', label: 'All' },
                                                    ...(filter.options || [])
                                                ] })) : (_jsx(Input, { type: filter.type, placeholder: filter.placeholder, value: filterValues[filter.key] || '', onChange: (e) => handleFilterChange(filter.key, e.target.value) }))] }, filter.key))) }), _jsxs("div", { className: "flex items-center justify-end gap-2", children: [_jsx(Button, { variant: "outline", onClick: handleClearFilters, size: isCompact ? "sm" : "default", children: "Clear Filters" }), _jsx(Button, { onClick: handleApplyFilters, size: isCompact ? "sm" : "default", children: "Apply Filters" })] })] })) })] })), bulkActions.length > 0 && bulkActionsStyle === 'toolbar' && (_jsx("div", { className: `border-b border-gray-200 dark:border-gray-700 transition-all duration-200 ease-in-out overflow-hidden ${hasSelectedRows
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 max-h-16 opacity-100'
                    : 'bg-gray-50 dark:bg-gray-700/50 max-h-0 opacity-0'}`, children: hasSelectedRows && (_jsxs("div", { className: "flex items-center justify-between px-6 py-3", children: [_jsxs("div", { className: "flex items-center space-x-4", children: [_jsxs("span", { className: "text-sm font-medium text-blue-700 dark:text-blue-300", children: [selectedRows.size, " item", selectedRows.size !== 1 ? 's' : '', " selected"] }), _jsx("button", { onClick: () => setSelectedRows(new Set()), className: "text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 underline", children: "Clear selection" })] }), _jsx("div", { className: "flex items-center space-x-2", children: bulkActions.map((action) => (_jsxs(Button, { variant: action.variant === 'danger' ? 'destructive' : (action.variant || "outline"), size: "sm", onClick: () => action.onClick(selectedItems), children: [typeof action.icon === 'function' ? null : action.icon, typeof action.label === 'function' ? action.label(null) : action.label] }, action.key))) })] })) })), _jsx(VirtualTableCore, { data: groupedData, columns: visibleColumnsArray, isCompact: isCompact, theme: theme, selectedRows: selectedRows, onRowSelect: handleRowSelect, onSelectAll: handleSelectAll, actions: actions, selectable: selectable, height: containerHeight, maxHeight: maxHeight, loading: loading, emptyMessage: `No ${title?.toLowerCase() || 'items'} found`, totalSelectableItems: sortedData.length, onGroupToggle: toggleGroupExpansion }), pagination && (_jsxs("div", { className: cn("border-t border-theme-border flex items-center justify-between", isCompact ? "px-4 py-3" : "px-6 py-4"), children: [_jsxs("div", { className: cn("text-theme-muted", isCompact ? "text-sm" : "text-base"), children: ["Showing ", ((pagination.page - 1) * pagination.pageSize) + 1, " to", ' ', Math.min(pagination.page * pagination.pageSize, pagination.total), " of", ' ', pagination.total, " results"] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Select, { value: { value: pagination.pageSize.toString(), label: pagination.pageSize.toString() }, onChange: (option) => pagination.onPageSizeChange(parseInt(option?.value || '10')), options: [
                                    { value: '10', label: '10' },
                                    { value: '25', label: '25' },
                                    { value: '50', label: '50' },
                                    { value: '100', label: '100' }
                                ] }), _jsx(Button, { variant: "outline", size: isCompact ? "sm" : "default", onClick: () => pagination.onPageChange(pagination.page - 1), disabled: pagination.page <= 1, children: "Previous" }), _jsxs("span", { className: cn("px-2 text-theme-foreground", isCompact ? "text-sm" : "text-base"), children: ["Page ", pagination.page, " of ", Math.ceil(pagination.total / pagination.pageSize)] }), _jsx(Button, { variant: "outline", size: isCompact ? "sm" : "default", onClick: () => pagination.onPageChange(pagination.page + 1), disabled: pagination.page >= Math.ceil(pagination.total / pagination.pageSize), children: "Next" })] })] }))] }));
});
VirtualDataTable.displayName = 'VirtualDataTable';
